import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  DataGrid,
  elGR,
  enUS,
  GridColDef,
  GridRenderCellParams,
  GridRowModel,
  GridSortModel,
} from '@mui/x-data-grid'
import { useSnackbar } from 'notistack'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, Outlet, useSearchParams } from 'react-router-dom'
import DotsLoading from 'assets/img/dots_loading.gif'
import { PropertyAdService } from 'api/services/PropertyAd'
import { ReactComponent as SpourgitiSvg } from 'assets/svg/icons/lists/g-spourgiti.svg'
import { ReactComponent as CircledDotsSvg } from 'assets/svg/new-icons/circled-dots.svg'
import { ReactComponent as FiltersSvg } from 'assets/svg/new-icons/filters.svg'
import { ReactComponent as TrashcanSvg } from 'assets/svg/new-icons/trashcan.svg'
import { CustomSwitch } from 'components/AgentProperties'
import Filters from 'components/AgentProperties/Filters'
import CustomTooltip from 'components/shared/CustomTooltip'
import { SOURCE_OPTIONS } from 'helpers/constants'
import CustomDialog from 'shared/CustomDialog'
import NoResultsOverlay from 'shared/table/overlays/NoResults'
import CustomPagination from 'shared/table/Pagination'
import ValidationList from 'shared/ValidationList'
import { PropertyAdBasicInfoDTO } from 'types'
import {
  PropertyAdStatusEnum,
  PropertyAdSubCategoryEnum,
  PropertyAdTypeEnum,
  PublishingStatusEnum,
} from 'types/enums'
import usePageView from 'hooks/usePageView'
import { useAuthStore } from 'store'

const PropertyAds: React.FC = () => {
  const { sendPageView } = usePageView({ runOnMount: false })
  const { t, i18n } = useTranslation()

  const theme = useTheme()

  const IS_LOCALE_GR = i18n.language === 'gr'
  const matchesSM = useMediaQuery(theme.breakpoints.down('md'))

  const { enqueueSnackbar } = useSnackbar()

  const [searchParams] = useSearchParams()
  const HAS_PARAM = searchParams.get('page')

  const [page, setPage] = React.useState(() =>
    HAS_PARAM ? Number(searchParams.get('page')) - 1 : 0
  )

  const [pageSize] = React.useState(10)
  const [results, setResults] = React.useState<PropertyAdBasicInfoDTO[]>([])
  const [rowCountState, setRowCountState] = React.useState(0)

  const [filterValue, setFilterValue] = React.useState<object | undefined>()

  const [sortValue, setSortValue] = React.useState<object | undefined>()
  const [filtersVisible, setFiltersVisible] = React.useState<boolean>(false)
  const [refreshRequired, setRefreshRequired] = React.useState<boolean>(false)

  const [openDeactivateDialog, setOpenDeactivateDialog] = React.useState(false)

  const currentUser = useAuthStore((state) => state.user)

  const CURRENT_USER_IS_ADMIN = currentUser?.role === 'ADMIN'

  const handleOpenDeactivateDialog = () => {
    setOpenDeactivateDialog(true)
  }
  const handleCloseDeactivateDialog = () => {
    setOpenDeactivateDialog(false)
  }

  const [selectedRow, setSelectedRow] = React.useState<
    PropertyAdBasicInfoDTO | undefined
  >()

  const handleChangeActiveStatus = (payload: PropertyAdBasicInfoDTO) => {
    setSelectedRow(payload)
    handleOpenDeactivateDialog()
  }

  const updateStatusMutation =
    PropertyAdService.hooks.useUpdatePropertyAdStatus({
      onSuccess: () => {
        handleCloseDeactivateDialog()
        setSelectedRow(undefined)
        enqueueSnackbar(t(`validation.successUpdateStatusPropertyAd`), {
          variant: 'success',
        })
        setRefreshRequired(true)
        countPropertyAds.refetch()
      },
      onError: () => {
        handleCloseDeactivateDialog()
        setSelectedRow(undefined)
        enqueueSnackbar(t('error.unspecific'), {
          variant: 'error',
        })
      },
    })

  const handleSubmitUpdateStatus = () => {
    if (selectedRow && selectedRow?.id && selectedRow?.propertyAdStatus) {
      updateStatusMutation.mutate({
        id: selectedRow?.id,
        status:
          selectedRow?.propertyAdStatus === PropertyAdStatusEnum.Active
            ? PropertyAdStatusEnum.Inactive
            : PropertyAdStatusEnum.Active,
      })
    }
  }

  const handleCloseDeactivate = () => {
    setSelectedRow(undefined)
    handleCloseDeactivateDialog()
  }

  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false)

  const handleOpenDeleteDialog = (payload: PropertyAdBasicInfoDTO) => {
    setSelectedRow(payload)
    setOpenDeleteDialog(true)
  }
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false)
    setSelectedRow(undefined)
  }

  const deletePropertyAdMutation = PropertyAdService.hooks.useDeletePropertyAd({
    onSuccess: () => {
      enqueueSnackbar(t(`validation.successDeletePropertyAd`), {
        variant: 'success',
      })
      handleCloseDeleteDialog()
      setRefreshRequired(true)
      countPropertyAds.refetch()
    },
    onError: () => {
      handleCloseDeleteDialog()
      enqueueSnackbar(t('error.unspecific'), {
        variant: 'error',
      })
    },
  })

  const handleSubmitDelete = () => {
    if (selectedRow && selectedRow?.id) {
      deletePropertyAdMutation.mutate(selectedRow?.id)
    }
  }

  const handleCloseDelete = () => {
    setSelectedRow(undefined)
    handleCloseDeleteDialog()
  }

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel.length) {
        // setFilterValue({})
        const model: { sortingField?: string; sortingOrder?: string } = {}
        switch (sortModel[0].field) {
          case 'source':
            model.sortingField = sortModel[0]?.field?.toUpperCase()
            model.sortingOrder = sortModel[0]?.sort?.toUpperCase()
            break
          case 'squareMeters':
            model.sortingField = 'SQUARE_METERS'
            model.sortingOrder = sortModel[0]?.sort?.toUpperCase()
            break
          case 'salesPrice':
            model.sortingField = 'PRICE'
            model.sortingOrder = sortModel[0]?.sort?.toUpperCase()
            break
          default:
            model.sortingField = sortModel[0].field
            model.sortingOrder = sortModel[0]?.sort?.toUpperCase()
            break
        }
        setSortValue(model)
        setPage(0)
      } else {
        setSortValue({})
      }
    },
    []
  )

  const countPropertyAds =
    PropertyAdService.hooks.useCountBasicInfoPropertyAds(filterValue)

  React.useEffect(() => {
    setRowCountState((prevRowCountState) =>
      countPropertyAds?.data !== undefined
        ? (countPropertyAds?.data as number)
        : prevRowCountState
    )
  }, [countPropertyAds.data, setRowCountState])

  const propertyAds = PropertyAdService.hooks.useBasicInfoPropertyAds(
    page,
    pageSize,
    filterValue,
    sortValue,
    {
      enabled: countPropertyAds.isSuccess && !!countPropertyAds.data,
      onError: () => {
        enqueueSnackbar(t('tables.responses.error') as unknown as string, {
          variant: 'error',
        })
      },
      onSettled() {
        sendPageView()
      },
    }
  )

  React.useEffect(() => {
    setResults((prevState) =>
      propertyAds?.data !== undefined ? propertyAds.data : []
    )
  }, [propertyAds.data, setResults])

  React.useEffect(() => {
    if (refreshRequired) {
      propertyAds.refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshRequired])

  const handlePageChange = (newPage) => {
    setPage(newPage)
  }

  const columns: GridColDef[] = [
    {
      field: 'status',
      flex: 1,
      headerName: t('tables.propertyAds.columns.status'),
      minWidth: 105,
      maxWidth: 150,
      headerClassName: 'table--header',
      type: 'singleSelect',
      hideable: false,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Tooltip
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'background.paper',
                  color: 'black',
                  fontSize: '15px',
                  border: (theme) => `1px solid ${theme.palette.primary.main}`,
                },
              },
            }}
            placement="right"
            title={
              params.row.propertyAdStatus === PropertyAdStatusEnum.Invalid ? (
                <ValidationList options={params?.row?.validationMessages} />
              ) : (
                ''
              )
            }
          >
            <FormControl variant="standard" sx={{}}>
              <CustomSwitch
                status={params.row.propertyAdStatus}
                disabled={[PropertyAdStatusEnum.Invalid].includes(
                  params.row.propertyAdStatus
                )}
                checked={[PropertyAdStatusEnum.Active].includes(
                  params.row.propertyAdStatus
                )}
                onChange={() => handleChangeActiveStatus(params?.row)}
              />
            </FormControl>
          </Tooltip>
        )
      },
    },
    {
      field: 'companyTitle',
      headerName: t('tables.agents.columns.name'),
      flex: 1,
      minWidth: 130,
      headerClassName: 'table--header',
      hideable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <CustomTooltip
            title={
              params?.row?.companyTitle
                ? params?.row?.companyTitle
                : ('-' as string)
            }
          >
            <Box sx={{ textOverflow: 'ellipsis' }}>
              {params?.row?.companyTitle}
            </Box>
          </CustomTooltip>
        )
      },
    },
    {
      field: 'externalId',
      headerName: t('tables.propertyAds.columns.name'),
      flex: 0.7,
      minWidth: 110,
      maxWidth: 135,
      headerClassName: 'table--header',
      hideable: false,
      sortable: false,
      filterable: false,
    },
    {
      field: 'estateAgentSource',
      headerName: t('tables.agents.columns.source'),
      flex: 1,
      minWidth: 130,
      maxWidth: 150,
      headerClassName: 'table--header',
      hideable: false,
      sortable: false,
      type: 'singleSelect',
      valueOptions: [
        ...SOURCE_OPTIONS.map((item) => ({
          value: item.value,
          label: t(item.label),
        })),
      ],
    },
    {
      field: 'propertyAdCategoryType',
      headerName: t('tables.propertyAds.columns.type'),
      flex: 1,
      minWidth: 100,
      headerClassName: 'table--header',
      hideable: false,
      sortable: false,
      type: 'singleSelect',
      filterable: false,
      valueGetter: (params) => {
        return t(
          `tables.propertyAds.categoryType.${params.row.propertyAdCategoryType.toLowerCase()}`
        )
      },
    },
    {
      field: 'propertyAdType',
      headerName: t('tables.propertyAds.columns.category'),
      flex: 1,
      minWidth: 135,
      headerClassName: 'table--header',
      hideable: false,
      sortable: false,
      filterable: false,
      valueGetter: (params) => {
        return t(
          `tables.propertyAds.adType.${params.row.propertyAdType.toLowerCase()}`
        )
      },
    },
    {
      field: 'propertySubCategory',
      headerName: t('tables.propertyAds.columns.subCategory'),
      flex: 1,
      minWidth: 135,
      headerClassName: 'table--header',
      hideable: false,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Typography fontSize={'14px'}>
            {params.row.propertyAdType !== PropertyAdTypeEnum.UNKNOWN &&
            params.row.propertySubCategory !== PropertyAdSubCategoryEnum.Unknown
              ? t(
                  `enums.ESTATE_PROPERTY_${
                    params.row.propertyAdType !== PropertyAdTypeEnum.PROPERTY
                      ? params.row.propertyAdType + '_'
                      : ''
                  }TYPE.${params.row.propertySubCategory.toLowerCase()}.base`
                )
              : t('tables.propertyAds.adType.unknown')}
          </Typography>
        )
      },
    },
    {
      field: 'area',
      headerName: t('tables.propertyAds.columns.area'),
      flex: 1,
      minWidth: 230,
      headerClassName: 'table--header',
      hideable: false,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <CustomTooltip title={params?.row?.location ?? ''}>
          <Box sx={{ textOverflow: 'ellipsis' }}>
            {params?.row?.location ?? '-'}
          </Box>
        </CustomTooltip>
      ),
    },
    {
      field: 'squareMeters',
      flex: 1,
      headerName: t('tables.propertyAds.columns.squareMeters'),
      minWidth: 80,
      headerClassName: 'table--header',
      type: 'number',
      hideable: false,
      sortable: true,
      filterable: false,
      valueGetter: (params) => {
        return params?.row.squareMeters != null
          ? t('generics.area', {
              count: Number(params?.row.squareMeters.toFixed(2)),
            })
          : '-'
      },
    },
    {
      field: 'salesPrice',
      flex: 1,
      headerName: t('tables.propertyAds.columns.price'),
      minWidth: 100,
      headerClassName: 'table--header',
      type: 'number',
      hideable: false,
      sortable: true,
      filterable: false,
      valueGetter: (params) => {
        return params?.row.salesPrice != null
          ? t('generics.currency', {
              count: Number(params?.row.salesPrice.toFixed(2)),
            })
          : '-'
      },
    },
    {
      field: 'views',
      headerName: t('tables.propertyAds.columns.views'),
      flex: 1,
      minWidth: 100,
      headerClassName: 'table--header',
      hideable: false,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <CustomTooltip
          title={`${t('tables.propertyAds.columns.weeklyViews')}: ${
            params?.row?.weeklyViews
          }`}
        >
          <Box>{params?.row?.views} </Box>
        </CustomTooltip>
      ),
    },
    {
      field: 'actions',
      flex: 1,
      headerClassName: 'table--header',
      type: 'actions',
      minWidth: 150,
      hideable: false,
      sortable: false,
      filterable: false,
      renderCell: (
        params: GridRenderCellParams<
          GridRowModel<PropertyAdBasicInfoDTO>,
          PropertyAdBasicInfoDTO
        >
      ) => (
        <>
          <Stack direction="row" spacing={1}>
            {!params.row.publishingStatus ||
            params.row.publishingStatus === PublishingStatusEnum.Success ||
            params.row.publishingStatus === PublishingStatusEnum.Failed ? (
              <>
                {params.row.propertyAdStatus === PropertyAdStatusEnum.Active ? (
                  <CustomTooltip
                    title={
                      t(
                        'tables.propertyAds.columns.actions.view'
                      ) as unknown as string
                    }
                  >
                    <Link
                      href={
                        process.env.REACT_APP_API_BASE_URL +
                        `property-ads/el:${params.row?.alternativeId}` +
                        `/#/property-ads/el:${params.row?.alternativeId}`
                      }
                      underline="none"
                      target="_blank"
                    >
                      <IconButton>
                        <SvgIcon
                          component={SpourgitiSvg}
                          inheritViewBox
                          sx={{ color: 'black' }}
                        />
                      </IconButton>
                    </Link>
                  </CustomTooltip>
                ) : (
                  <CustomTooltip
                    title={
                      t(
                        'tables.propertyAds.columns.actions.inactiveProperty'
                      ) as unknown as string
                    }
                  >
                    <span>
                      <IconButton disabled>
                        <SvgIcon component={SpourgitiSvg} inheritViewBox />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                )}
                <CustomTooltip
                  title={
                    (t(
                      'tables.propertyAds.columns.actions.edit'
                    ) as unknown as string) ?? ''
                  }
                >
                  <Link
                    component={RouterLink}
                    to={
                      !CURRENT_USER_IS_ADMIN
                        ? `${params.row?.id}`
                        : `/property-ads/${params.row?.id}`
                    }
                    underline="none"
                  >
                    <IconButton color="info">
                      <SvgIcon
                        component={CircledDotsSvg}
                        inheritViewBox
                        sx={{ color: '#ffffff' }}
                      />
                    </IconButton>
                  </Link>
                </CustomTooltip>
                <CustomTooltip
                  title={
                    (t(
                      'tables.propertyAds.columns.actions.delete'
                    ) as unknown as string) ?? ''
                  }
                >
                  <IconButton
                    color="error"
                    onClick={() => handleOpenDeleteDialog(params.row)}
                  >
                    <SvgIcon
                      component={TrashcanSvg}
                      inheritViewBox
                      sx={{ color: '#ffffff' }}
                    />
                  </IconButton>
                </CustomTooltip>
              </>
            ) : [
                PublishingStatusEnum.Pending,
                PublishingStatusEnum.InProgress,
              ].includes(params.row.publishingStatus) ? (
              <CustomTooltip
                title={
                  t(
                    'tables.propertyAds.tooltips.PublishingStatusPending'
                  ) as unknown as string
                }
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    component="img"
                    src={DotsLoading}
                    sx={{ width: 50, height: 50, mixBlendMode: 'overlay' }}
                  />
                </Box>
              </CustomTooltip>
            ) : null}
          </Stack>
        </>
      ),
    },
  ]

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        px: { xs: 2, sm: 2, md: 3, lg: 4, xl: 6 },
        pt: 1,
        pb: 4,
        borderRadius: 2.5,
        mx: 'auto',
      }}
    >
      <Grid
        item
        container
        direction={matchesSM ? 'column-reverse' : 'row'}
        justifyContent={matchesSM ? 'flex-end' : 'end '}
        alignItems={matchesSM ? 'end' : 'start'}
        sx={{ mt: '15px' }}
      >
        <Grid
          item
          sx={{
            display: filtersVisible ? 'default' : 'none',
            mr: 0,
          }}
        >
          <Filters
            onFilterChange={(filters) => {
              setFilterValue(filters)
              setPage(0)
              countPropertyAds.refetch()
            }}
          />
        </Grid>
        {matchesSM ? null : (
          <Divider
            orientation="vertical"
            variant="fullWidth"
            flexItem
            sx={{
              mx: 2,
              bgcolor: 'black',
              display: filtersVisible ? 'default' : 'none',
            }}
          />
        )}
        <Grid
          item
          sx={{
            mb: matchesSM && filtersVisible ? '16px' : null,
            alignItems: 'start',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            sx={{
              boxShadow: '3px 3px 8px 0px #0909094D',
              borderRadius: '8px',
              px: '24px',
              ml: matchesSM && filtersVisible ? ' 8px' : 0,
            }}
            onClick={() => {
              setFiltersVisible(!filtersVisible)
            }}
          >
            <SvgIcon component={FiltersSvg} inheritViewBox />
          </Button>
        </Grid>
      </Grid>
      <Stack>
        <Box
          sx={{
            width: '100%',
            mt: 2,
          }}
        >
          <div style={{ display: 'flex', height: '100%' }}>
            <Box
              sx={{
                flexGrow: 1,
                '& .table--header': {
                  color: 'textPrimary',
                  fontSize: '1rem',
                  fontWeight: 600,
                },
              }}
            >
              <DataGrid
                key={i18n.language}
                localeText={
                  IS_LOCALE_GR
                    ? elGR.components.MuiDataGrid.defaultProps.localeText
                    : enUS.components.MuiDataGrid.defaultProps.localeText
                }
                autoHeight
                disableSelectionOnClick
                disableColumnMenu
                disableColumnSelector={true}
                rows={results}
                rowCount={rowCountState}
                columns={columns}
                page={page}
                onPageChange={handlePageChange}
                pageSize={pageSize}
                rowsPerPageOptions={[25]}
                components={{
                  LoadingOverlay: LinearProgress,
                  NoRowsOverlay: NoResultsOverlay,
                  Pagination: CustomPagination,
                }}
                loading={
                  countPropertyAds.isLoading ||
                  propertyAds.isLoading ||
                  propertyAds.isFetching
                }
                paginationMode="server"
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-footerContainer': {
                    justifyContent: 'center',
                  },
                  '& .MuiPagination-root': {
                    mt: '40px',
                  },
                }}
              />
            </Box>
          </div>
        </Box>
        <Outlet />
      </Stack>
      <CustomDialog
        open={openDeactivateDialog}
        onClose={handleCloseDeactivate}
        onSubmit={handleSubmitUpdateStatus}
        titleLabel={
          selectedRow?.propertyAdStatus &&
          selectedRow?.propertyAdStatus === PropertyAdStatusEnum.Active
            ? t(`tables.propertyAds.dialogs.title`)
            : t(`tables.propertyAds.dialogs.activate.title`)
        }
        subtitleLabel={
          selectedRow?.propertyAdStatus &&
          selectedRow?.propertyAdStatus === PropertyAdStatusEnum.Active
            ? t(`tables.propertyAds.dialogs.subtitle`)
            : t(`tables.propertyAds.dialogs.activate.subtitle`)
        }
        cancelLabel={t(`tables.propertyAds.dialogs.cancel`)}
        submitLabel={t(`tables.propertyAds.dialogs.continue`)}
      />
      <CustomDialog
        open={openDeleteDialog}
        onClose={handleCloseDelete}
        onSubmit={handleSubmitDelete}
        titleLabel={t(`tables.propertyAds.dialogs.delete.title`)}
        subtitleLabel={t(`tables.propertyAds.dialogs.delete.subtitle`)}
        cancelLabel={t(`tables.propertyAds.dialogs.delete.cancel`)}
        submitLabel={t(`tables.propertyAds.dialogs.delete.continue`)}
      />
    </Box>
  )
}

export default PropertyAds
